import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import { Alert, lighten, Stack, Typography, useTheme } from "@mui/material";
import type { MandatoryExtraInfo } from "@/utils/extras";
import { formatCurrency } from "@/utils/pricing";

interface MandatoryExtrasInfoBoxProps {
  extras: MandatoryExtraInfo[];
  currency: string;
  currencyAlt: string | undefined;
}

export default function MandatoryExtrasInfoBox({
  extras,
  currency,
  currencyAlt,
}: MandatoryExtrasInfoBoxProps) {
  const { i18n } = useLingui();
  const theme = useTheme();

  const priceNotZero = extras.filter((extra) => extra.price > 0);
  const priceZero = extras.filter((extra) => extra.price === 0);

  return (
    <Alert
      severity="info"
      sx={{
        bgcolor: { xs: lighten(theme.palette.primary.main, 0.85), sm: "grey.100" },
      }}
    >
      <Stack spacing={1.5}>
        {priceNotZero.length > 0 && (
          <div>
            <Typography variant="body1">
              <Trans>Already included in the total price:</Trans>
            </Typography>
            <Typography component="ul" variant="body1" sx={{ pl: 2 }}>
              {priceNotZero.map((extra) => (
                <li key={extra.id}>
                  {extra.name}: {formatCurrency(extra.price, currency, i18n.locale)}
                  {currencyAlt && extra.priceAlt !== 0 && (
                    <Typography color="text.secondary" variant="body1" component="span">
                      {" "}
                      ({formatCurrency(extra.priceAlt, currencyAlt, i18n.locale)})
                    </Typography>
                  )}
                </li>
              ))}
            </Typography>
          </div>
        )}
        {priceZero.length > 0 && (
          <div>
            <Typography variant="body1">
              <Trans>Mandatory charges to be paid on site:</Trans>
            </Typography>
            <Typography component="ul" variant="body1" sx={{ pl: 2 }}>
              {priceZero.map((extra) => (
                <li key={extra.id}>{extra.name}</li>
              ))}
            </Typography>
          </div>
        )}
      </Stack>
    </Alert>
  );
}
